/*
 * Money that works anywhere: no currency, tax name or tax treatment is assumed.
 *
 *  - Every amount is stored with its basis: whether it includes tax ("inc") or not ("ex"), and the
 *    tax rate that applies to it. ServiceM8 does the same for items (price_includes_taxes +
 *    tax_rate_uuid), so an Australian item "$110 inc GST" and a US one "$100 + sales tax" are
 *    both just amounts in a basis.
 *  - Calculations are done before tax ("net"), converting only at the edges: reading an amount
 *    (toNet) and writing or showing one (fromNet). Tax is never profit.
 *  - Formatting follows the business (currency and number style from its ServiceM8 region), and
 *    tax is named with the rate's own name from ServiceM8 ("GST", "VAT", "Sales Tax").
 *
 * Shared by the server and the browser: no imports.
 */

/** How a business writes money, from its ServiceM8 account (server/business.ts). */
export interface MoneyFormat {
    /** BCP 47 locale, e.g. "en-AU"; null = the browser's own. */
    locale: string | null;
    /** ISO 4217 code, e.g. "AUD"; null = unknown (amounts are shown without a currency symbol). */
    currency: string | null;
    /** ServiceM8's default for new items: amounts shown with tax included. */
    amountsIncludeTax: boolean;
}

/** A tax rate as ServiceM8 has it. */
export interface TaxRate {
    uuid: string;
    /** e.g. "GST", "VAT", "Sales Tax", "No tax": a short name given it here (the Price List's settings), else its own. */
    name: string;
    /** Its name as ServiceM8 has it, when `name` is a short name given here. */
    fullName?: string;
    /** Percentage, e.g. 10 for 10%. */
    percent: number;
    /** The account's default: used by items with no tax rate of their own. */
    isDefault: boolean;
}

/** A number from a decimal string or number; anything unreadable is 0. */
export function toNumber(v: unknown): number {
    let n = Number.NaN;
    if (typeof v === "number") n = v;
    else if (typeof v === "string") n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

/** Rounded to the cent (half away from zero, without float surprises like 1.005 -> 1.00). */
export function roundCents(v: number): number {
    return Math.sign(v) * Math.round(Math.abs(v) * 100 + 1e-9) / 100;
}

/** A money amount as ServiceM8 stores it: a decimal string to the cent ("12.50"). */
export const toCents = (v: number): string => roundCents(v).toFixed(2);

/**
 * A stored amount (before tax), to 4 decimals: enough that converting it back to a tax-included
 * amount lands on the same cent ($11.00 inc 10% = 10.0000 net).
 */
export function toStored(v: number): string {
    const s = (Math.round(v * 10000) / 10000).toFixed(4);
    // Trailing zeros off ("10.5000" -> "10.5", "10.0000" -> "10").
    let end = s.length;
    while (s[end - 1] === "0") end--;
    if (s[end - 1] === ".") end--;
    const out = s.slice(0, end);
    return out === "-0" ? "0" : out;
}

/** An item's tax rate: its own, else the account's default rate, else none (0%). */
export function rateFor(taxRateUuid: string | null | undefined, rates: readonly TaxRate[]): TaxRate | null {
    return (taxRateUuid ? rates.find((r) => r.uuid === taxRateUuid) : undefined) ?? rates.find((r) => r.isDefault) ?? null;
}

/** A tax rate as a fraction, e.g. 0.1 for 10% (0 for none). */
export const fraction = (rate: TaxRate | null | undefined) => (rate ? rate.percent / 100 : 0);

/** An amount in a basis, before tax. */
export const toNet = (amount: number, includesTax: boolean, taxFraction: number) => (includesTax ? amount / (1 + taxFraction) : amount);

/** A before-tax amount, in a basis. */
export const fromNet = (net: number, includesTax: boolean, taxFraction: number) => (includesTax ? net * (1 + taxFraction) : net);

/** Formats money for one business. */
export function moneyFormatter(format: MoneyFormat) {
    const locale = format.locale ?? undefined;
    const currency = format.currency
        ? new Intl.NumberFormat(locale, { style: "currency", currency: format.currency })
        : new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    return {
        /** "$1,234.50" (or "1,234.50" when the currency isn't known). */
        money: (v: number | string) => currency.format(toNumber(v)),
        /** "1,234.5" */
        number: (v: number | string) => number.format(toNumber(v)),
        /** "10%" */
        percent: (v: number) => `${number.format(v)}%`,
        /** The currency symbol on its own, e.g. "$" or "£" ("" when unknown), for labels and inputs. */
        symbol: format.currency
            ? (currency.formatToParts(0).find((p) => p.type === "currency")?.value ?? "")
            : "",
    };
}
export type MoneyFormatter = ReturnType<typeof moneyFormatter>;

/** "inc GST" / "ex GST", or "no tax" for a 0% (or missing) rate. `full`: with the rate's name as ServiceM8 has it ("inc GST on Income"), e.g. on hover. */
export function basisLabel(includesTax: boolean, rate: TaxRate | null | undefined, full = false): string {
    if (!rate || rate.percent === 0) return "no tax";
    return `${includesTax ? "inc" : "ex"} ${full ? (rate.fullName ?? rate.name) : rate.name}`;
}
